import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { dateStrInAppTz } from "../../../utils/appTime";
import { withAuth } from "../../../components/HOC/withAuth";
import { useExchangeRateOptional } from "../../../contexts/exchangeRate";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Label from "../../../components/BaseUI/Label";
import Lozenge from "../../../components/BaseUI/Lozenge";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
// Shared price display: follows the system USD/TL toggle in the header
import ProductPrice from "../../../components/CustomUI/ProductPrice";
import {
  FiShoppingCart,
  FiCalendar,
  FiDollarSign,
  FiCreditCard,
  FiTrash2,
  FiClock,
  FiServer,
  FiPackage,
  FiCheckCircle,
  FiChevronDown,
  FiRefreshCw,
} from "react-icons/fi";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const EditDeviceSale = () => {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/device-sales",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  const format = useFormatValue;

  const [models, setModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [showModelPicker, setShowModelPicker] = useState(false);

  const [saleData, setSaleData] = useState({
    saleName: "",
    companyId: null,
    sequence: "",
    saleDate: dateStrInAppTz(new Date()),
    saleTime: new Date().toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    }),
    totalDevices: 0,
    paymentMethod: "cash",
    paymentStatus: "pending",
    notes: "",
    exchangeRate: "",
  });

  const fx = useExchangeRateOptional();

  const parseExchangeRate = (val) => {
    if (val === undefined || val === null || val === "") return undefined;
    const num = Number(String(val).trim().replace(",", "."));
    return !isNaN(num) && num > 0 ? num : undefined;
  };

  useEffect(() => {
    if (!saleId) return;
    fetchSaleData();
    fetchModels();
    fetchCompanies();
  }, [saleId]);

  useEffect(() => {
    updateTotalDevices();
  }, [selectedModels]);

  const getMonthYearFromDate = (dateStr) => {
    const d = dateStr ? new Date(dateStr) : new Date();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear() % 100).padStart(2, "0");
    return `${month}${year}`; // MMYY
  };

  const buildSaleName = (companyCode, dateStr, sequence) => {
    if (!companyCode || !sequence) return "";
    const monthYear = getMonthYearFromDate(dateStr);
    return `${companyCode}/${monthYear}/${sequence}`;
  };

  const fetchSaleData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/device-sales/${saleId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );
      const sale = response.data?.sale || response.data;

      if (sale?.approvalStatus === "pending") {
        setToasts([
          {
            id: Date.now(),
            title: translate("UI.ERROR") || "Error!",
            description: "Onay bekleyen siparis düzenlenemez.",
            appearance: "danger",
          },
        ]);
        setTimeout(() => navigate("/device-sales"), 1500);
        return;
      }

      // Try to extract sequence from saleName formatted as "<companyCode>/<MMYY>/<sequence>"
      const parsedSequence = (() => {
        const saleName = sale?.saleName || "";
        const parts = saleName.split("/").filter(Boolean);
        const last = parts[parts.length - 1] || "";
        const num = parseInt(last, 10);
        if (!Number.isFinite(num) || num <= 0) return "";
        return num.toString();
      })();

      let resolvedExchangeRate =
        sale.exchangeRate !== undefined && sale.exchangeRate !== null
          ? String(sale.exchangeRate)
          : "";

      if (!resolvedExchangeRate) {
        // Base rate on Sipariş Tarihi (saleDate), fallback to createdAt
        const baseDate = sale.saleDate || sale.createdAt;
        if (baseDate) {
          try {
            const dateStr =
              typeof baseDate === "string"
                ? baseDate.split("T")[0]
                : new Date(baseDate).toISOString().split("T")[0];
            const fxRes = await axios.get(
              `${API_URL}/api/exchange-rate/by-date?date=${dateStr}`,
              { headers: { Authorization: `Bearer ${getToken()}` } },
            );
            if (fxRes.data?.rate) {
              resolvedExchangeRate = String(fxRes.data.rate);
            }
          } catch (e) {
            console.warn(
              "Could not fetch historical exchange rate on edit:",
              e,
            );
          }
        }
      }

      // Convert sale data to form structure
      setSaleData({
        saleName: sale.saleName || "",
        companyId:
          typeof sale.companyId === "object"
            ? sale.companyId?._id
            : sale.companyId,
        saleDate: sale.saleDate
          ? dateStrInAppTz(new Date(sale.saleDate))
          : dateStrInAppTz(new Date()),
        saleTime:
          sale.saleTime ||
          new Date().toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
          }),
        sequence: parsedSequence,
        totalDevices: sale.totalDevices || 0,
        paymentMethod: sale.paymentMethod || "cash",
        paymentStatus: sale.paymentStatus || "pending",
        notes: sale.notes || "",
        exchangeRate: resolvedExchangeRate,
      });

      // Convert models from sale to selectedModels format
      if (sale.models && Array.isArray(sale.models)) {
        const modelsData = sale.models.map((m) => {
          const modelIdObj = typeof m.modelId === "object" ? m.modelId : null;
          return {
            modelId: modelIdObj?._id || m.modelId,
            code: modelIdObj?.code || m.code || "",
            name: modelIdObj?.name || m.name || "",
            count: m.count || 1,
            price: modelIdObj?.price || m.price || 0,
          };
        });
        setSelectedModels(modelsData);
      }
    } catch (error) {
      console.error("Failed to fetch sale:", error);
      setToasts([
        {
          description:
            error.response?.data?.message || "Failed to load sale data",
          id: Date.now(),
          title: translate("UI.ERROR") || "Error!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/device-models`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setModels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch models:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const handleAddModel = (model) => {
    const existingIndex = selectedModels.findIndex(
      (m) => m.modelId === model._id,
    );
    if (existingIndex >= 0) {
      const updated = [...selectedModels];
      updated[existingIndex].count += 1;
      setSelectedModels(updated);
    } else {
      setSelectedModels([
        ...selectedModels,
        {
          modelId: model._id,
          code: model.code,
          name: model.name,
          count: 1,
          price: model.price || 0,
        },
      ]);
    }
    setShowModelPicker(false);
    updateTotalDevices();
  };

  const handleRemoveModel = (modelId) => {
    setSelectedModels(selectedModels.filter((m) => m.modelId !== modelId));
    updateTotalDevices();
  };

  const handleUpdateModelCount = (modelId, count) => {
    if (count <= 0) {
      handleRemoveModel(modelId);
      return;
    }
    const updated = selectedModels.map((m) =>
      m.modelId === modelId ? { ...m, count } : m,
    );
    setSelectedModels(updated);
    updateTotalDevices();
  };

  const updateTotalDevices = () => {
    const total = selectedModels.reduce((sum, m) => sum + m.count, 0);
    setSaleData((prev) => ({ ...prev, totalDevices: total }));
  };

  const calculateTotalAmount = () => {
    return selectedModels.reduce((sum, m) => {
      const price =
        typeof m.modelId === "object" && m.modelId?.price
          ? m.modelId.price
          : m.price || 0;
      return sum + price * m.count;
    }, 0);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (!saleData.companyId) {
        setToasts([
          {
            description:
              translate("UI.SELECT_COMPANY_REQUIRED") ||
              "Lutfen bir cari secin.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      const company = companies.find((c) => c._id === saleData.companyId);
      const fallbackSaleName = company?.code
        ? buildSaleName(company.code, saleData.saleDate, saleData.sequence)
        : "";

      const saleNameToUse = saleData.saleName?.trim()
        ? saleData.saleName
        : fallbackSaleName;

      if (!saleNameToUse || saleNameToUse.trim() === "") {
        setToasts([
          {
            description:
              translate("UI.SALE_NAME_REQUIRED") || "Satis adi giriniz.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      if (selectedModels.length === 0) {
        setToasts([
          {
            description:
              translate("UI.PLEASE_SELECT_MODELS") ||
              translate("UI.PLEASE_SELECT_DEVICES") ||
              "En az bir model seciniz.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      if (!saleData.paymentMethod || saleData.paymentMethod.trim() === "") {
        setToasts([
          {
            description:
              translate("UI.PAYMENT_METHOD_REQUIRED") ||
              "Please select payment method.",
            id: Date.now(),
            title: "Error!",
            appearance: "danger",
          },
        ]);
        return;
      }

      const salePayload = {
        saleName: saleNameToUse,
        saleDate: saleData.saleDate,
        saleTime: saleData.saleTime,
        companyId: saleData.companyId || undefined,
        totalDevices: saleData.totalDevices,
        totalAmount: calculateTotalAmount(),
        exchangeRate: parseExchangeRate(saleData.exchangeRate),
        models: selectedModels.map((m) => ({
          modelId: m.modelId,
          count: m.count,
        })),
        paymentMethod: saleData.paymentMethod,
        paymentStatus: saleData.paymentStatus,
        notes: saleData.notes || "",
      };

      const response = await axios.put(
        `${API_URL}/api/device-sales/${saleId}`,
        salePayload,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description:
              response.data.message || "Satis başarıyla güncellendi.",
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);

        setTimeout(() => {
          navigate("/device-sales");
        }, 1500);
      }
    } catch (error) {
      console.error("Satis güncellenirken bir hata oluştu:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Satis güncellenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const handleCompanySelect = (company) => {
    if (!company || !company._id) {
      console.error("Company or company._id is undefined");
      return;
    }
    setSaleData((prevState) => ({
      ...prevState,
      companyId: company._id,
    }));
  };

  const handleDateChange = async (value) => {
    setSaleData((prevState) => ({
      ...prevState,
      saleDate: value,
    }));
    if (value) {
      try {
        const rateRes = await axios.get(
          `${API_URL}/api/exchange-rate/by-date?date=${value}`,
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        if (rateRes.data?.rate) {
          setSaleData((prev) => ({
            ...prev,
            exchangeRate: String(rateRes.data.rate),
          }));
        }
      } catch (err) {
        console.warn("Could not fetch rate for date:", err);
      }
    }
  };

  const handleSaleTimeChange = (value) => {
    setSaleData((prevState) => ({
      ...prevState,
      saleTime: value,
    }));
  };

  const handlePaymentMethodChange = (method) => {
    setSaleData((prevState) => ({
      ...prevState,
      paymentMethod: method,
    }));
  };

  const handlePaymentStatusChange = (status) => {
    setSaleData((prevState) => ({
      ...prevState,
      paymentStatus: status,
    }));
  };

  const handleNotesChange = (value) => {
    setSaleData((prevState) => ({
      ...prevState,
      notes: value,
    }));
  };

  const getPaymentStatusAppearance = (status) => {
    switch (status) {
      case "paid":
        return "success-subtle";
      case "pending":
        return "warning-subtle";
      case "partial":
        return "info-subtle";
      case "refunded":
        return "danger-subtle";
      default:
        return "neutral-subtle";
    }
  };

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.DEVICE_SALES"), url: "/device-sales" },
    { label: translate("UI.EDIT") },
  ];

  if (!canWrite) {
    return (
      <AppPage title={translate("UI.EDIT_SALE")} breadcrumbs={breadcrumbs}>
        <Container>
          <ContainerItem>
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>Bu sayfada degisiklik yapma yetkiniz yok.</span>
              <Button
                appearance="primary-animated"
                onClick={() => navigate("/device-sales")}
              >
                Geri Don
              </Button>
            </div>
          </ContainerItem>
        </Container>
      </AppPage>
    );
  }

  return (
    <AppPage title={translate("UI.EDIT_SALE")} breadcrumbs={breadcrumbs}>
      <style>
        {`
          .app-page-header {
            margin-bottom: 1.5rem;
          }
        `}
      </style>
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card shadow-sm modern-card">
            <div className="card-body p-4">
              <>
                {/* Sale Information */}
                <div className="section-container">
                  <div className="section-header">
                    <div className="section-icon">
                      <FiShoppingCart className="text-primary" />
                    </div>
                    <div className="section-title flex-grow-1">
                      <h5 className="mb-1">
                        {translate("UI.SALE_INFORMATION")}
                      </h5>
                      <p className="text-muted small mb-0">
                        {translate("UI.SALE_INFO_DESC")}
                      </p>
                    </div>
                  </div>

                  <div className="section-content">
                    <div className="d-flex justify-content-around flex-wrap gap-1">
                      <div className="col-md-4 w-45">
                        <Label>
                          {translate("UI.SELECT_COMPANY")}{" "}
                          <span className="text-danger ms-1">*</span>
                        </Label>
                        <div className="form-control-container">
                          <Dropdown
                            required
                            trigger={
                              <div className="d-flex align-items-center justify-content-between p-2">
                                <span className="dropdown-text">
                                  {
                                    companies.find(
                                      (company) =>
                                        company._id === saleData.companyId,
                                    )?.name
                                  }
                                </span>
                              </div>
                            }
                          >
                            <DropdownContent>
                              {/* <DropdownItem
                              onClick={() =>
                                setSaleData((prev) => ({
                                  ...prev,
                                  companyId: null,
                                }))
                              }
                            >
                              <i>{translate("UI.NO_COMPANY")}</i>
                            </DropdownItem> */}
                              {companies && companies.length > 0 ? (
                                companies.map((company) =>
                                  company && company._id ? (
                                    <DropdownItem
                                      key={company._id}
                                      onClick={() =>
                                        handleCompanySelect(company)
                                      }
                                      active={
                                        company._id === saleData.companyId
                                      }
                                    >
                                      {company.name} ({company.code})
                                    </DropdownItem>
                                  ) : null,
                                )
                              ) : (
                                <DropdownItem disabled>
                                  {translate("UI.NO_COMPANIES_AVAILABLE")}
                                </DropdownItem>
                              )}
                            </DropdownContent>
                          </Dropdown>
                        </div>
                      </div>

                      <div className="col-md-4 w-45 mb-3">
                        <Label>
                          {translate("UI.SALE_NAME") || "Sale Name"}{" "}
                          <span className="text-danger ms-1">*</span>
                        </Label>
                        <Input
                          type="text"
                          value={saleData.saleName}
                          onChange={(value) =>
                            setSaleData((prev) => ({
                              ...prev,
                              saleName: value,
                            }))
                          }
                          placeholder={
                            translate("UI.ENTER_SALE_NAME") || "Enter sale name"
                          }
                        />
                      </div>

                      <div className="col-md-4 w-45">
                        <Input
                          type="date"
                          label={translate("UI.SALE_DATE")}
                          value={saleData.saleDate}
                          onChange={handleDateChange}
                          required
                          prefix={<FiCalendar className="text-muted" />}
                          hint={translate("UI.SALE_DATE_DESC")}
                        />
                      </div>
                      <div className="col-md-4 w-45">
                        <Input
                          type="time"
                          label={translate("UI.SALE_TIME")}
                          value={saleData.saleTime}
                          onChange={handleSaleTimeChange}
                          required
                          prefix={<FiClock className="text-muted" />}
                          hint={translate("UI.SALE_TIME_DESC")}
                        />
                      </div>
                      <div className="col-md-4 w-45">
                        <Input
                          type="text"
                          inputMode="decimal"
                          label={
                            translate("UI.EXCHANGE_RATE") ||
                            "Döviz Kuru (USD/TRY)"
                          }
                          value={saleData.exchangeRate}
                          onChange={(value) =>
                            setSaleData((prev) => ({
                              ...prev,
                              exchangeRate: value,
                            }))
                          }
                          prefix={<FiDollarSign className="text-muted" />}
                          placeholder={fx?.rate ? String(fx.rate) : "42.156"}
                          hint="TCMB USD/TRY döviz kuru (Örn: 42.156 veya 42,156)"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Model Selection Section */}
                <div className="section-container">
                  <div className="section-header">
                    <div className="section-icon">
                      <FiServer className="text-info" />
                    </div>
                    <div className="section-title flex-grow-1">
                      <h5 className="mb-1">
                        {translate("UI.SELECT_MODELS") ||
                          translate("UI.SELECT_DEVICES")}
                      </h5>
                    </div>
                    <Button
                      appearance="primary"
                      onClick={() => setShowModelPicker(true)}
                      icon={<FiPackage />}
                    >
                      {translate("UI.ADD_MODEL") ||
                        translate("UI.ADD") ||
                        "Add Model"}
                    </Button>
                  </div>

                  <div className="section-content">
                    {selectedModels.length === 0 ? (
                      <div className="empty-models-container">
                        <FiServer size={64} className="text-muted opacity-50" />
                        <h6 className="text-muted mb-1 mt-3">
                          {translate("UI.NO_MODELS_SELECTED") ||
                            translate("UI.NO_DEVICES_SELECTED")}
                        </h6>
                        <p className="text-muted small mb-0">
                          {translate("UI.PLEASE_SELECT_MODELS") ||
                            translate("UI.PLEASE_SELECT_DEVICES") ||
                            "Click 'Add Model' to select models"}
                        </p>
                      </div>
                    ) : (
                      <div className="models-grid">
                        {selectedModels.map((item) => (
                          <div key={item.modelId} className="model-card">
                            <div className="model-card-header">
                              <div className="model-icon-wrapper">
                                <FiPackage className="model-icon" />
                              </div>
                              <div className="model-info flex-grow-1">
                                <div className="model-code">{item.code}</div>
                                <div className="model-name">{item.name}</div>
                              </div>
                              <IconButton
                                icon={<FiTrash2 />}
                                appearance="danger-subtle"
                                onClick={() => handleRemoveModel(item.modelId)}
                                title={translate("UI.REMOVE")}
                              />
                            </div>
                            <div className="model-card-divider"></div>
                            <div className="model-card-body">
                              <div className="model-detail-row">
                                <span className="detail-label">
                                  {translate("UI.QUANTITY")}:
                                </span>
                                <div className="count-container">
                                  <Button
                                    appearance="subtle"
                                    size="sm"
                                    onClick={() =>
                                      handleUpdateModelCount(
                                        item.modelId,
                                        Math.max(1, item.count - 1),
                                      )
                                    }
                                  >
                                    -
                                  </Button>
                                  <Input
                                    type="number"
                                    value={item.count.toString()}
                                    onChange={(value) => {
                                      const num =
                                        parseInt(
                                          value.replace(/[^0-9]/g, ""),
                                        ) || 1;
                                      handleUpdateModelCount(item.modelId, num);
                                    }}
                                    min={1}
                                    className="count-input"
                                  />
                                  <Button
                                    appearance="subtle"
                                    size="sm"
                                    onClick={() =>
                                      handleUpdateModelCount(
                                        item.modelId,
                                        item.count + 1,
                                      )
                                    }
                                  >
                                    +
                                  </Button>
                                </div>
                              </div>
                              <div className="model-detail-row">
                                <span className="detail-label">
                                  {translate("UI.PRICE_PER_UNIT")}:
                                </span>
                                <span className="model-price">
                                  <ProductPrice value={item.price || 0} />
                                </span>
                              </div>
                              <div className="model-total-row">
                                <span className="detail-label fw-bold">
                                  {translate("UI.TOTAL")}:
                                </span>
                                <span className="model-total">
                                  {format(
                                    (item.price || 0) * item.count,
                                    "currency",
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedModels.length > 0 && (
                      <div className="total-amount-container">
                        <div className="total-amount-header">
                          <span className="total-amount-label">
                            {translate("UI.TOTAL_AMOUNT")}:
                          </span>
                        </div>
                        <div className="total-amount-row">
                          <span className="total-amount-value">
                            <ProductPrice
                              value={calculateTotalAmount()}
                              rate={
                                parseExchangeRate(saleData.exchangeRate) ??
                                fx?.rate
                              }
                            />
                          </span>
                        </div>
                        {(parseExchangeRate(saleData.exchangeRate) ||
                          fx?.rate) && (
                          <div className="exchange-rate-info text-muted small mt-2">
                            <span>
                              {translate("UI.EXCHANGE_RATE") || "Döviz Kuru"}: 1
                              USD ={" "}
                              {Number(
                                parseExchangeRate(saleData.exchangeRate) ??
                                  fx?.rate,
                              ).toFixed(4)}{" "}
                              TL
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Information */}
                <div className="section-container">
                  <div className="section-header">
                    <div className="section-icon">
                      <FiCreditCard className="text-success" />
                    </div>
                    <div className="section-title flex-grow-1">
                      <h5 className="mb-1">
                        {translate("UI.PAYMENT_INFORMATION")}
                      </h5>
                      <p className="text-muted small mb-0">
                        {translate("UI.PAYMENT_METHOD_DESC")}
                      </p>
                    </div>
                  </div>

                  <div className="section-content">
                    <div className="row g-3 mb-4">
                      <div className="col-md-6 col-12">
                        <Label>{translate("UI.PAYMENT_METHOD")}</Label>
                        <div className="form-control-container">
                          <Dropdown
                            trigger={
                              <div className="d-flex align-items-center justify-content-between p-2">
                                <div className="d-flex align-items-center gap-2">
                                  <FiDollarSign className="text-muted" />
                                  <span>
                                    {translate(
                                      `UI.${saleData.paymentMethod.toUpperCase()}`,
                                    )}
                                  </span>
                                </div>
                              </div>
                            }
                          >
                            <DropdownContent>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentMethodChange("cash")
                                }
                                active={saleData.paymentMethod === "cash"}
                              >
                                <FiDollarSign className="me-2" />
                                {translate("UI.CASH")}
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentMethodChange("credit_card")
                                }
                                active={
                                  saleData.paymentMethod === "credit_card"
                                }
                              >
                                <FiCreditCard className="me-2" />
                                {translate("UI.CREDIT_CARD")}
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentMethodChange("bank_transfer")
                                }
                                active={
                                  saleData.paymentMethod === "bank_transfer"
                                }
                              >
                                <FiCreditCard className="me-2" />
                                {translate("UI.BANK_TRANSFER")}
                              </DropdownItem>
                            </DropdownContent>
                          </Dropdown>
                        </div>
                      </div>
                      <div className="col-md-6 col-12 mt-2">
                        <Label>{translate("UI.PAYMENT_STATUS")}</Label>
                        <div className="form-control-container">
                          <Dropdown
                            trigger={
                              <div className="d-flex align-items-center justify-content-between p-2">
                                <Lozenge
                                  appearance={getPaymentStatusAppearance(
                                    saleData.paymentStatus,
                                  )}
                                >
                                  {translate(
                                    `UI.${saleData.paymentStatus.toUpperCase()}`,
                                  )}
                                </Lozenge>
                              </div>
                            }
                          >
                            <DropdownContent>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentStatusChange("pending")
                                }
                                active={saleData.paymentStatus === "pending"}
                              >
                                <Lozenge appearance="warning-subtle">
                                  {translate("UI.PENDING")}
                                </Lozenge>
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentStatusChange("paid")
                                }
                                active={saleData.paymentStatus === "paid"}
                              >
                                <Lozenge appearance="success-subtle">
                                  {translate("UI.PAID")}
                                </Lozenge>
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentStatusChange("partial")
                                }
                                active={saleData.paymentStatus === "partial"}
                              >
                                <Lozenge appearance="info-subtle">
                                  {translate("UI.PARTIAL")}
                                </Lozenge>
                              </DropdownItem>
                            </DropdownContent>
                          </Dropdown>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <Input
                          type="textarea"
                          label={translate("UI.NOTES")}
                          value={saleData.notes}
                          onChange={handleNotesChange}
                          hint={translate("UI.NOTES_DESC")}
                          rows={4}
                          placeholder={
                            translate("UI.ENTER_NOTES") ||
                            "Enter additional notes..."
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="form-actions">
                  <Button
                    appearance="danger-subtle"
                    onClick={() => navigate("/device-sales")}
                    title={translate("UI.CANCEL")}
                  >
                    {translate("UI.CANCEL")}
                  </Button>
                  <Button
                    appearance="primary-animated"
                    onClick={handleSubmit}
                    loading={loading}
                    icon={<FiShoppingCart />}
                    title={translate("UI.SAVE")}
                  >
                    {translate("UI.SAVE")}
                  </Button>
                </div>
              </>
            </div>
          </div>
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
            autoDismiss={true}
          />
        ))}
      </ToastContainer>

      {/* Model Picker Modal */}
      <Modal
        isOpen={showModelPicker}
        onClose={() => setShowModelPicker(false)}
        scrollBehavior="inside"
        width={"medium"}
      >
        <ModalHeader>
          <div className="d-flex align-items-center gap-2">
            <div className="modal-header-icon">
              <FiPackage />
            </div>
            <div>
              <h5 className="mb-0">
                {translate("UI.SELECT_MODELS") ||
                  translate("UI.SELECT_DEVICES") ||
                  "Select Models"}
              </h5>
              <small className="text-muted">
                {translate("UI.SELECT_MODELS_DESC") ||
                  translate("UI.SELECT_DEVICES_DESC") ||
                  "Choose models to add to sale"}
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          {models.length === 0 ? (
            <div className="empty-models-container">
              <FiServer size={48} className="text-muted opacity-50" />
              <p className="text-muted mb-0 mt-2">
                {translate("UI.NO_MODELS_AVAILABLE") || "No models available"}
              </p>
            </div>
          ) : (
            models.map((model) => (
              <div
                key={model._id}
                className="model-picker-item"
                onClick={() => handleAddModel(model)}
              >
                <div className="d-flex align-items-center gap-3 flex-grow-1">
                  <div className="model-picker-icon">
                    <FiPackage />
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">
                      {model.code} - {model.name}
                    </div>
                    <div className="text-muted small">
                      <ProductPrice value={model.price || 0} />
                    </div>
                  </div>
                </div>
                <div className="model-picker-action">
                  <FiCheckCircle className="text-primary" />
                </div>
              </div>
            ))
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="danger-subtle"
            onClick={() => setShowModelPicker(false)}
          >
            {translate("UI.CLOSE") || "Close"}
          </Button>
        </ModalFooter>
      </Modal>
      <style>
        {`
                .modern-card {
                    border-radius: 12px;
                    border: 1px solid #e9ecef;
                    overflow: hidden;
                }

                .section-container {
                    margin-bottom: 2rem;
                    padding: 1.75rem;
                    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
                    border-radius: 12px;
                    border: 1px solid #e9ecef;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                    transition: all 0.3s ease;
                }

                .section-container:hover {
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
                }

                .section-container:last-child {
                    margin-bottom: 0;
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-bottom: 1.75rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #f1f3f5;
                }

                .section-icon {
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #00b4f3 0%, #006C80 100%);
                    border-radius: 12px;
                    box-shadow: 0 4px 12px rgba(12, 132, 121, 0.2);
                    flex-shrink: 0;
                }

                .section-icon svg {
                    width: 24px;
                    height: 24px;
                    color: white;
                }

                .section-title {
                    flex: 1;
                }

                .section-title h5 {
                    font-size: 1.15rem;
                    font-weight: 600;
                    margin: 0 0 0.25rem 0;
                    color: #1a202c;
                }

                .section-title p {
                    margin: 0;
                    line-height: 1.4;
                }

                .section-content {
                    padding: 0.5rem 0 0 0;
                }

                .form-control-container {
                    background: #fff;
                    border: 1px solid #e2e5e8;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }

                .form-control-container:hover {
                    border-color: #00b4f3;
                    box-shadow: 0 0 0 3px rgba(12, 132, 121, 0.1);
                }

                .dropdown-text {
                    flex: 1;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .empty-models-container {
                    min-height: 240px;
                    border: 2px dashed #dee2e6;
                    border-radius: 16px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: #94a3b8;
                    gap: 0.5rem;
                    padding: 3rem 2rem;
                    background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
                }

                .empty-models-container h6 {
                    color: #495057;
                    font-weight: 600;
                }

                .models-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 1.25rem;
                }

                @media (max-width: 768px) {
                    .models-grid {
                        grid-template-columns: 1fr;
                    }
                }

                .model-card {
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    padding: 0;
                    background: #fff;
                    transition: all 0.3s ease;
                    overflow: hidden;
                }

                .model-card:hover {
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                    transform: translateY(-4px);
                }

                .model-card-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 1rem;
                    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                }

                .model-icon-wrapper {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #00b4f3 0%, #006C80 100%);
                    border-radius: 10px;
                    box-shadow: 0 4px 12px rgba(12, 132, 121, 0.2);
                    flex-shrink: 0;
                }

                .model-icon {
                    width: 20px;
                    height: 20px;
                    color: white;
                }

                .model-info {
                    flex: 1;
                    min-width: 0;
                }

                .model-code {
                    font-size: 14px;
                    font-weight: 700;
                    margin-bottom: 4px;
                    color: #1a202c;
                }

                .model-name {
                    font-size: 13px;
                    color: #6c757d;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .model-card-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, #e9ecef, transparent);
                }

                .model-card-body {
                    padding: 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .model-detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 0.5rem;
                }

                .detail-label {
                    font-size: 13px;
                    color: #6c757d;
                    font-weight: 500;
                }

                .count-container {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .count-input {
                    width: 50px !important;
                    text-align: center;
                    font-weight: 600;
                    padding: 0 !important;
                }

                .count-input input {
                    text-align: center;
                    padding: 0.25rem 0.5rem;
                    font-weight: 600;
                }

                .model-price {
                    font-size: 14px;
                    font-weight: 600;
                    color: #00b4f3;
                }

                .model-total-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-top: 0.75rem;
                    border-top: 1px solid #e9ecef;
                }

                .model-total {
                    font-size: 16px;
                    font-weight: 700;
                    color: #00b4f3;
                }

                .total-amount-container {
                    margin-top: 1.5rem;
                    padding: 1.25rem;
                    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                    border: 2px solid #00b4f3;
                    border-radius: 12px;
                }

                .total-amount-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.75rem;
                }

                .total-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .total-amount-value-usd {
                    font-size: 20px;
                    font-weight: 600;
                    color: #059669;
                }

                .exchange-rate-info {
                    padding-top: 0.5rem;
                    border-top: 1px solid #e9ecef;
                    text-align: center;
                }

                .total-amount-label {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1a202c;
                }

                .total-amount-value {
                    font-size: 24px;
                    font-weight: 700;
                    color: #00b4f3;
                }

                @media (max-width: 576px) {
                    .total-amount-container {
                        padding: 1rem;
                    }

                    .total-amount-label {
                        font-size: 16px;
                    }

                    .total-amount-value {
                        font-size: 20px;
                    }
                }

                .model-picker-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 1.25rem;
                    border: 1px solid #e9ecef;
                    border-radius: 10px;
                    margin-bottom: 0.75rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: #fff;
                }

                .model-picker-item:hover {
                    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                    border-color: #00b4f3;
                    box-shadow: 0 4px 12px rgba(12, 132, 121, 0.15);
                    transform: translateX(4px);
                }

                .model-picker-item .fw-semibold {
                    color: #1a202c;
                    font-weight: 600;
                }

                .model-picker-item .text-muted {
                    color: #00b4f3;
                }

                .model-picker-item svg {
                    transition: transform 0.3s ease;
                }

                .model-picker-item:hover svg {
                    transform: scale(1.1);
                }

                .modal-header-icon {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #00b4f3 0%, #006C80 100%);
                    border-radius: 10px;
                    box-shadow: 0 4px 12px rgba(12, 132, 121, 0.2);
                }

                .modal-header-icon svg {
                    width: 20px;
                    height: 20px;
                    color: white;
                }

                .model-picker-icon {
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
                    border-radius: 8px;
                    flex-shrink: 0;
                }

                .model-picker-icon svg {
                    width: 18px;
                    height: 18px;
                    color: #6c757d;
                }

                .model-picker-action {
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                .model-picker-item:hover .model-picker-action {
                    opacity: 1;
                }

                .model-picker-action svg {
                    width: 24px;
                    height: 24px;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    gap: 1rem;
                    margin-top: 0;
                    padding: 1.5rem;
                    border-top: 2px solid #f1f3f5;
                    background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
                    border-radius: 0 0 12px 12px;
                }

                @media (max-width: 576px) {
                    .form-actions {
                        flex-direction: column-reverse;
                        gap: 0.75rem;
                    }

                    .form-actions button {
                        width: 100%;
                    }
                }

                @media (max-width: 768px) {
                    .section-container {
                        padding: 1.25rem;
                    }

                    .section-header {
                        flex-direction: row;
                        align-items: center;
                    }

                    .section-icon {
                        width: 42px;
                        height: 42px;
                    }

                    .section-title h5 {
                        font-size: 1rem;
                    }

                    .row.g-3 {
                        gap: 1rem !important;
                    }
                }

                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .section-container {
                    animation: slideIn 0.4s ease-out;
                }

                .model-card {
                    animation: slideIn 0.3s ease-out;
                }

                input:focus,
                textarea:focus,
                select:focus {
                    outline: none;
                    border-color: #00b4f3 !important;
                    box-shadow: 0 0 0 3px rgba(12, 132, 121, 0.1) !important;
                }
                `}
      </style>
    </AppPage>
  );
};

// CSS ekleyelim
const styles = `
.single-select .select__option {
    cursor: pointer;
    padding: 8px 12px;
}

.single-select .select__option:hover {
    background-color: var(--bs-primary-subtle);
}

.single-select .select__option--is-selected {
    background-color: var(--bs-primary-subtle);
    color: var(--bs-primary);
}

.single-select .select__option--is-focused {
    background-color: var(--bs-primary-subtle);
}

.single-select .select__multi-value {
    background-color: var(--bs-primary-subtle);
    color: var(--bs-primary);
}
`;

// Style tag'ini head'e ekle
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default withAuth(EditDeviceSale, ["admin", "employee", "user"]);
